// F-V: progress + results for a batch narration run.
//
// KL 2026-10-05 (option 1): the progress opens in the CENTRE of the screen
// right after Generate, with a prominent "keep this page open" reminder.
// "Continue working" (or closing the box) shrinks it to a corner pill that
// keeps a short reminder; clicking the pill brings the centre view back.
// When the run ends with failures or unstarted slots, the results open in
// the centre too (all-success shows a toast instead).

import { useEffect, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { Button } from "@shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/components/ui/dialog";
import {
  narrationEditPath,
  planNarration,
  type NarrationKind,
} from "@shared/api/narration";
import {
  useNarrationRun,
  type RunItem,
  type RunOutcome,
} from "./NarrationRunContext";
import { LANG_LABEL, type NarrationLang } from "./NarrationBadges";

const langLabel = (l: string) => LANG_LABEL[l as NarrationLang] ?? l;

export const KEEP_OPEN_REMINDER =
  "Keep this page open until audio generation is complete. Closing or reloading the tab stops it.";

/** Re-plan the retry set: only slots still missing, and only if the budget
 *  still covers them. Returns null (with a toast) when it can't run. */
async function planRetry(items: RunItem[]): Promise<RunItem[] | null> {
  const kinds = [...new Set(items.map((i) => i.kind))] as NarrationKind[];
  const wanted = new Set(
    items.map((i) => `${i.kind}:${i.ownerId}:${i.language}`),
  );
  const retry: RunItem[] = [];
  let budget: {
    usedChars: number;
    budgetChars: number;
    generationEnabled: boolean;
  } | null = null;
  for (const kind of kinds) {
    const plan = await planNarration(kind, [
      ...new Set(items.filter((i) => i.kind === kind).map((i) => i.ownerId)),
    ]);
    budget = plan.budget;
    for (const i of plan.items) {
      if (
        i.status === "missing" &&
        wanted.has(`${kind}:${i.ownerId}:${i.language}`)
      )
        retry.push({
          kind,
          ownerId: i.ownerId,
          code: i.code,
          titleTc: i.titleTc,
          language: i.language,
          chars: i.chars,
        });
    }
  }
  if (!budget) return retry;
  const { usedChars, budgetChars, generationEnabled } = budget;
  const needed = retry.reduce((n, i) => n + i.chars, 0);
  if (!generationEnabled) {
    toast.error("AI narration is switched off by an admin");
    return null;
  }
  if (needed > budgetChars - usedChars) {
    toast.error(
      `Needs ${needed.toLocaleString()} characters, but only ${Math.max(
        budgetChars - usedChars,
        0,
      ).toLocaleString()} are left this month`,
    );
    return null;
  }
  return retry;
}

const NarrationRunPill: FC = () => {
  const navigate = useNavigate();
  const { run, stop, dismiss, start } = useNarrationRun();
  const [minimized, setMinimized] = useState(false);
  const [retrying, setRetrying] = useState(false);

  // Every new run opens in the centre.
  const runId = run?.id;
  useEffect(() => {
    setMinimized(false);
  }, [runId]);

  if (!run) return null;

  const running = run.phase === "running";
  const pct = run.total ? Math.round((run.done / run.total) * 100) : 0;
  const problems: RunOutcome[] = [...run.failed, ...run.notStarted];

  const openOwner = (item: RunItem) => {
    dismiss();
    navigate(narrationEditPath(item.kind, item.ownerId));
  };

  const retry = async () => {
    setRetrying(true);
    try {
      const items = await planRetry(problems.map((p) => p.item));
      if (items === null) return;
      dismiss();
      if (items.length) start(items);
      else toast.success("Nothing left to generate");
    } catch {
      toast.error("Could not re-check the plan");
    } finally {
      setRetrying(false);
    }
  };

  const progressBar = (
    <div className="h-2 rounded bg-muted">
      <div
        className="h-2 rounded bg-primary transition-all"
        style={{ width: `${pct}%` }}
      />
    </div>
  );

  return (
    <>
      {/* Centre view while running */}
      <Dialog
        open={running && !minimized}
        onOpenChange={(open) => !open && setMinimized(true)}
      >
        <DialogContent data-testid="narration-run-dialog">
          <DialogHeader>
            <DialogTitle>Generating Narration Audio</DialogTitle>
            <DialogDescription data-testid="narration-run-progress">
              {run.done} of {run.total} narration audio generated
            </DialogDescription>
          </DialogHeader>
          {progressBar}
          <p
            className="text-sm text-muted-foreground"
            data-testid="narration-run-current"
          >
            {run.current
              ? `Now: ${langLabel(run.current.language)} · ${run.current.titleTc || run.current.code}`
              : ""}
          </p>
          <p
            className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
            data-testid="narration-run-reminder"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            {KEEP_OPEN_REMINDER}
          </p>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={stop}
              data-testid="narration-run-stop"
            >
              Stop
            </Button>
            <Button
              type="button"
              onClick={() => setMinimized(true)}
              data-testid="narration-run-minimize"
            >
              Continue working ↘
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Corner pill once minimised */}
      {running && minimized && (
        <button
          type="button"
          onClick={() => setMinimized(false)}
          className="fixed inset-x-0 bottom-0 z-40 border-t bg-white px-4 py-3 text-left shadow-lg sm:inset-x-auto sm:bottom-4 sm:right-4 sm:w-80 sm:rounded-lg sm:border"
          title="Show progress"
          data-testid="narration-run-pill"
        >
          <span className="flex items-center justify-between text-sm font-medium">
            <span>Generating narration audio</span>
            <span data-testid="narration-run-pill-progress">
              {run.done} / {run.total}
            </span>
          </span>
          <span className="mt-2 block">{progressBar}</span>
          <span className="mt-2 flex items-center gap-1 text-xs text-amber-800">
            <AlertTriangle className="h-3 w-3" />
            Keep this page open
          </span>
        </button>
      )}

      {/* Results (only when something failed or didn't start) */}
      <Dialog open={!running} onOpenChange={(open) => !open && dismiss()}>
        <DialogContent data-testid="narration-result-dialog">
          <DialogHeader>
            <DialogTitle>Narration audio finished</DialogTitle>
            <DialogDescription data-testid="narration-run-summary">
              ✓ {run.succeeded.length} generated · ✕ {run.failed.length} failed
              · ◌ {run.notStarted.length} not started
            </DialogDescription>
          </DialogHeader>
          <ul className="max-h-72 overflow-y-auto divide-y text-sm">
            {problems.map(({ item, message }) => (
              <li
                key={`${item.ownerId}:${item.language}`}
                className="flex items-start gap-3 py-2"
                data-testid={`narration-result-${item.ownerId}-${item.language}`}
              >
                <span className="font-mono">{item.code || "---"}</span>
                <span className="flex-1">
                  <span className="block">
                    {item.titleTc || "—"}{" "}
                    <span className="text-muted-foreground">
                      {langLabel(item.language)}
                    </span>
                  </span>
                  <span className="block text-red-600">{message}</span>
                </span>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  onClick={() => openOwner(item)}
                  data-testid={`narration-result-edit-${item.ownerId}-${item.language}`}
                >
                  Edit
                </Button>
              </li>
            ))}
          </ul>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={dismiss}
              data-testid="narration-result-close"
            >
              Close
            </Button>
            <Button
              type="button"
              onClick={() => void retry()}
              disabled={retrying}
              data-testid="narration-result-retry"
            >
              Retry failed + not started ({problems.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NarrationRunPill;
